import { useEffect, useState } from 'react';
import { admin, type SiteDayHours, type SiteHoursWrite }
  from '../lib/api.ts';

/**
 * A week of opening hours for one site.
 *
 * Three states per day, not two. "Default" means no row at all, so the
 * site keeps inheriting and a client who never opens this screen is
 * unaffected; "Closed" is a row that says shut, which is the only thing
 * that can beat the inherited value.
 *
 * Monday first. The product is read by shop managers, and nobody there
 * thinks of the week as starting on Sunday -- even though the database
 * numbers it that way, which is why dow travels with each row.
 */
const DAYS: ReadonlyArray<readonly [number, string]> = [
  [1, 'Monday'], [2, 'Tuesday'], [3, 'Wednesday'], [4, 'Thursday'],
  [5, 'Friday'], [6, 'Saturday'], [0, 'Sunday'],
];

type Mode = 'open' | 'closed' | 'default';

interface Row {
  readonly dow: number;
  readonly mode: Mode;
  /** Kept while the row is Closed or Default, so switching back to
      Open restores what was there instead of an empty box. */
  readonly opens: string;
  readonly closes: string;
  readonly inherited: string;
}

const toRow = (d: SiteDayHours): Row => ({
  dow: d.dow,
  mode: !d.has_row ? 'default' : d.closed ? 'closed' : 'open',
  opens: d.opens_at ?? d.effective_opens ?? '07:30',
  closes: d.closes_at ?? d.effective_closes ?? '18:00',
  inherited: d.effective_opens && d.effective_closes
    ? `${d.effective_opens} to ${d.effective_closes}`
    : 'closed',
});

export const WeeklyHours = ({ siteId, timezone }: {
  readonly siteId: string;
  readonly timezone: string;
}) => {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setRows(null);
    setSaved(false);
    setError('');
    if (!siteId) return;
    admin
      .hours(siteId)
      .then((r) => setRows(r.days.map(toRow)))
      .catch(() => setError('Could not load this site’s hours.'));
  }, [siteId]);

  const at = (dow: number) => rows?.find((r) => r.dow === dow);

  const edit = (dow: number, patch: Partial<Row>) => {
    setSaved(false);
    setRows((rs) =>
      (rs ?? []).map((r) => (r.dow === dow ? { ...r, ...patch } : r)));
  };

  /** Most shops run one weekday pattern, so typing it five times is
      the kind of chore that gets done wrong on the fourth row. */
  const copyWeekdays = () => {
    const mon = at(1);
    if (!mon) return;
    setSaved(false);
    setRows((rs) => (rs ?? []).map((r) =>
      r.dow >= 2 && r.dow <= 5
        ? { ...r, mode: mon.mode, opens: mon.opens, closes: mon.closes }
        : r));
  };

  const save = async () => {
    if (!rows) return;
    setBusy(true);
    setError('');
    try {
      // Default days are simply absent: the server replaces the week
      // with what it is sent, so leaving one out deletes its row and
      // hands the day back to the fallback.
      const days: SiteHoursWrite[] = rows
        .filter((r) => r.mode !== 'default')
        .map((r) => r.mode === 'closed'
          ? { dow: r.dow, closed: true }
          : { dow: r.dow, opensAt: r.opens, closesAt: r.closes });
      await admin.saveHours(siteId, days);
      // Re-read rather than trust the local copy, so the inherited
      // column reflects what the server actually resolved.
      setRows((await admin.hours(siteId)).days.map(toRow));
      setSaved(true);
    } catch {
      setError('That did not save.');
    } finally {
      setBusy(false);
    }
  };

  if (!rows) {
    return <p className="hint">{error || 'Loading hours…'}</p>;
  }

  return (
    <div className="hoursGrid">
      {DAYS.map(([dow, label]) => {
        const r = at(dow);
        if (!r) return null;
        // A window whose end is earlier than its start runs past
        // midnight. Saying so stops it reading as a typo.
        const overnight = r.mode === 'open' && r.closes <= r.opens;
        return (
          <div className="hoursRow" key={dow}>
            <label className="fieldLab">{label}</label>
            <select
              value={r.mode}
              disabled={busy}
              onChange={(e) =>
                edit(dow, { mode: e.target.value as Mode })}
            >
              <option value="open">Open</option>
              <option value="closed">Closed</option>
              <option value="default">Default</option>
            </select>
            {r.mode === 'open' ? (
              <>
                <input
                  type="time"
                  value={r.opens}
                  disabled={busy}
                  onChange={(e) => edit(dow, { opens: e.target.value })}
                />
                <span className="hint">to</span>
                <input
                  type="time"
                  value={r.closes}
                  disabled={busy}
                  onChange={(e) => edit(dow, { closes: e.target.value })}
                />
                {overnight && (
                  <span className="hint">ends next morning</span>
                )}
              </>
            ) : (
              <span className="hint">
                {r.mode === 'closed'
                  ? 'Shut all day'
                  : `Default: ${r.inherited}`}
              </span>
            )}
          </div>
        );
      })}

      {error && <p className="err">{error}</p>}

      <div className="noticeRow">
        <button disabled={busy} onClick={copyWeekdays}>
          Copy Monday to Tue&ndash;Fri
        </button>
        <button className="primary" disabled={busy} onClick={save}>
          {busy ? 'Saving…' : 'Save hours'}
        </button>
        {saved && <span className="hint">Saved.</span>}
        <span className="hint">All times in {timezone}.</span>
      </div>
    </div>
  );
};
